/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable no-use-before-define,no-param-reassign,no-mixed-operators,class-methods-use-this
 */
import $ from 'jquery';
// @ts-ignore
import biComponentErrorFactory from 'js-sdk/src/common/bi/error/biComponentErrorFactory';
// @ts-ignore
import request from 'js-sdk/src/common/transport/request';
import _ from 'underscore';
import { IMultiAxisDataService } from './ServicesTypes';
import { IState } from '../store/types/StateTypes';
import {
    IMultiAxisDataServiceDataLayout,
    IMultiAxisDataServiceDataLayoutAggregation,
    IMultiAxisDataServiceDataLayoutItem,
    IMultiAxisDataset,
    IMultiAxisDatasetNode,
    MultiAxisDataServiceDataLayoutAxis,
    MultiAxisDatasetData
} from '../store/types/MultiAxisDatasetTypes';
import i18n from '../../i18n/AdHocBundle.properties';

const MAX_AMOUNT = 2147483647;

type ExecutionState = 'new' | 'existing' | 'deleted';

type DatasetDataNode = {
    memberIdx?: number,
    dataIdx?: number,
    all: boolean,
    children?: DatasetDataNode[]
}

type DatasetLevelItem = {
    level?: {
        referenceObject: {
            name: string
        },
        type: string,
        members: string[]
    },
    aggregation?: {
        members: string[]
    }
}

type Execution = {
    layout: IMultiAxisDataServiceDataLayout,
    state: ExecutionState,
    id: JQueryDeferred<string>,
    calls: {
        [_: string]: JQueryDeferred<any>,
    },
    server:string
}

const emptyAxis = {
    axisNode: { all: true, children: [] },
    levels: []
};

function paginationCode(colsOffset: number, rowsOffset: number, colsLimit: number, rowsLimit: number): string {
    return [colsOffset, rowsOffset, colsLimit, rowsLimit].join('x');
}

function convertPathItemsIntoServerSideFormat(order :any) {
    if (!order || !order.path) {
        return order;
    }

    return {
        ...order,
        path: order.path.map((pathItem :string) => pathItem.replace('total_node', 'All'))
    };
}

function generateOrderBy(dataLayout: IMultiAxisDataServiceDataLayout) {
    const allowedLevels = [...dataLayout.columns.items, ...dataLayout.rows.items]
        .filter((item) => (!item.measuresLevel))
        .map((item) => (item.id || item.hierarchicalName));

    const levels = dataLayout.order.levels.map((ord: any) => ({ level: ord })).filter((level: any) => (
        allowedLevels.find((id) => (
            id === level.level.fieldRef
        ))
    ));

    return [
        ...levels,
        ...dataLayout.order.members.map(convertPathItemsIntoServerSideFormat).map((ord: any) => ({ member: ord })),
        ...dataLayout.order.bottomN.map(convertPathItemsIntoServerSideFormat).map((ord: any) => ({ bottomN: ord })),
        ...dataLayout.order.topN.map(convertPathItemsIntoServerSideFormat).map((ord: any) => ({ topN: ord }))
    ];
}

export function generateQuery(dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, rowsCount: number, columnsCount: number, state: IState) {
    const {
        rows, columns, aggregations, limit
    } = dataLayout;
    const toLevelItem = (item: IMultiAxisDataServiceDataLayoutItem) => {
        let res;
        if (item.measuresLevel) {
            res = { aggregations: {} };
        } else {
            res = {
                level: {
                    includeAll: true,
                    dimension: item.hierarchicalName,
                    field: item.hierarchicalName,
                    categorizer: item.categorizer
                }
            } as any;

            if (item.id && (item.id !== item.hierarchicalName)) {
                res.level.id = item.id;
            }
        }

        return res;
    };

    const toLevelExpansion = (item: IMultiAxisDataServiceDataLayoutItem) => ({
        level: {
            expanded: item.expanded,
            fieldRef: item.id || item.hierarchicalName,
            aggregation: item.measuresLevel
        }
    });

    const toSelectItem = (aggregation: IMultiAxisDataServiceDataLayoutAggregation) => {
        const res = {
            type: aggregation.aggregationType,
            functionName: aggregation.aggregationFunctionName,
            fieldRef: aggregation.field,
            timeBalanceFunctionName: aggregation.timeBalanceFunctionName
        } as any;

        if (aggregation.id && (aggregation.id !== aggregation.field)) {
            res.id = aggregation.id;
        }

        return res;
    };

    const toMemberExpansion = (expansion: any) => ({
        member: {
            ...expansion,
            path: expansion.path.map((segment: any) => (segment || 'empty_node'))
        }
    });

    const rowExpansions = rows.items.map(toLevelExpansion).concat(rows.expansions.map(toMemberExpansion) as any);
    const colExpansions = columns.items.map(toLevelExpansion).concat(columns.expansions.map(toMemberExpansion) as any);
    const orderBy = generateOrderBy(dataLayout);

    const result = {
        params: {
            offset: [startIndexColumns, startIndexRows],
            pageSize: [columnsCount || MAX_AMOUNT, rowsCount || MAX_AMOUNT]
        },
        dataSource: { reference: state.resourceURI },
        query: {
            select: { aggregations: aggregations.map(toSelectItem) },
            groupBy: {
                rows: {
                    items: rows.items.map(toLevelItem),
                    expansions: rowExpansions
                },
                columns: {
                    items: columns.items.map(toLevelItem),
                    expansions: colExpansions
                }
            }
        }
    } as any;

    if (dataLayout.filters.expression || dataLayout.filters.parameters.length) {
        result.query.where = {
            filterExpression: dataLayout.filters.expression,
            parameters: dataLayout.filters.parameters.map((param: any) => ({
                name: param.name,
                expression: param.value
            }))
        }
    }

    if (orderBy.length) {
        result.query.orderBy = orderBy;
    }

    if (!_.isUndefined(limit) && limit >= 0) {
        result.query.limit = limit;
    }

    return result;
}

function convertNode(node: DatasetDataNode, levelItems: DatasetLevelItem[], axisItems: IMultiAxisDataServiceDataLayoutItem[], currentDepth: number): IMultiAxisDatasetNode {
    const total = node.children && node.children.find((child) => (child.all));
    const children = node.children && node.children.filter((child) => (!child.all));
    const members = currentDepth ? (levelItems[currentDepth - 1].level || levelItems[currentDepth - 1].aggregation)!.members : [];

    const label :any = node.memberIdx === undefined ? i18n['adhoc.node.total.node'] : members[node.memberIdx];

    return {
        index: node.children ? -1 : -2,
        expanded: axisItems[currentDepth] && axisItems[currentDepth].expanded,
        isMeasure: axisItems[currentDepth - 1] && axisItems[currentDepth - 1].measuresLevel,
        label,
        children: children ? children.map((child) => (convertNode(child, levelItems, axisItems, currentDepth + 1))) : [],
        total: total ? convertNode(total, levelItems, axisItems, currentDepth + 1) : null,
        isTotal: node.all,
        isFirst: false,
        isLast: false
    }
}

function putTotals(node: IMultiAxisDatasetNode) {
    if (node.total) {
        node.children.push(node.total);
    }

    node.children.forEach(putTotals);
}

function setFirstAndLast(node: IMultiAxisDatasetNode, isZero: boolean, isEnd: boolean, isNotFirst: boolean, isNotLast: boolean) {
    let child;
    if (isEnd) {
        node.isLast = true;
    }
    if (isZero) {
        node.isFirst = true;
    }
    if (isNotFirst) {
        child = node;
        while (child.children.length) {
            [child] = child.children;
            child.isFirst = true;
        }
    }
    if (isNotLast) {
        child = node;
        while (child.children.length) {
            child = child.children[child.children.length - 1];
            child.isLast = true;
        }
    }
    if (node.children.length) {
        setFirstAndLast(node.children[0], isZero, false, false, node.children.length > 1);
        for (let i = 1; i < node.children.length - 1; i += 1) {
            setFirstAndLast(node.children[i], false, false, true, true);
        }
        setFirstAndLast(node.children[node.children.length - 1], false, isEnd, node.children.length > 1, false);
    }
}

function extractTotals(node: IMultiAxisDatasetNode) {
    node.children.forEach(extractTotals);

    if (node.total) {
        node.children.pop();

        const last = node.children[node.children.length - 1];

        if (last) {
            last.isLast = true;
            last.isFirst = last.isFirst || node.total.isFirst;
        }
    }
}

function setExpanded(node: IMultiAxisDatasetNode, path: string[], depth: number, expanded: boolean) {
    if (depth === path.length) {
        node.expanded = expanded;
    } else {
        let child = node.children.find((cd) => (cd.label === path[depth]));
        if (!child && path[depth] === 'All') {
            child = node.total as IMultiAxisDatasetNode;
        }

        if (child) {
            if (expanded) {
                child.expanded = true;
            }
            setExpanded(child, path, depth + 1, expanded);
        }
    }
}

export function areAxisSame(oldRow: MultiAxisDataServiceDataLayoutAxis, newRow: MultiAxisDataServiceDataLayoutAxis): boolean {
    let areSame = oldRow.expansions.length === newRow.expansions.length && oldRow.items.length === newRow.items.length;

    if (areSame) {
        areSame = oldRow.items.reduce((memo: boolean, oldItem, index) => (
            memo && (oldItem.expanded === newRow.items[index].expanded)
            && (oldItem.field === newRow.items[index].field)
            && (oldItem.id === newRow.items[index].id)
            && (oldItem.categorizer === newRow.items[index].categorizer)
            && (oldItem.hierarchicalName === newRow.items[index].hierarchicalName)
        ), true);

        if (areSame) {
            areSame = oldRow.expansions.reduce((memo: boolean, oldExp, index) => (
                memo && (oldExp.expanded === newRow.expansions[index].expanded)
                && (oldExp.path.length === newRow.expansions[index].path.length)
                && newRow.expansions[index].path.reduce((memo2: boolean, pathItem, pathIndex) => (
                    memo2 && pathItem === oldExp.path[pathIndex]
                ), true)
            ), true);
        }
    }

    return areSame;
}

export function areLayoutsSame(oldDataLayout: IMultiAxisDataServiceDataLayout, newDataLayout: IMultiAxisDataServiceDataLayout) {
    return oldDataLayout && newDataLayout
        && oldDataLayout.filters.expression === newDataLayout.filters.expression
        && newDataLayout.filters.parameters === oldDataLayout.filters.parameters
        && newDataLayout.order === oldDataLayout.order
        && newDataLayout.limit === oldDataLayout.limit
        && areAxisSame(oldDataLayout.rows, newDataLayout.rows)
        && areAxisSame(oldDataLayout.columns, newDataLayout.columns);
}

function convertResponseAxis(datasetAxi: any, axis: MultiAxisDataServiceDataLayoutAxis, isZero: boolean, isEnd: boolean) {
    const datasetAxis = datasetAxi || emptyAxis;
    const root = convertNode(datasetAxis.axisNode, datasetAxis.levels, axis.items, 0);
    axis.expansions.forEach((expansion) => { setExpanded(root, expansion.path, 0, expansion.expanded) });

    putTotals(root);
    setFirstAndLast(root, isZero, isEnd, false, false);
    extractTotals(root);

    root.label = '';
    root.index = -1;
    root.isTotal = false;
    root.isFirst = true;
    root.isLast = true;

    // first level expand/collapse hack
    root.expanded = true;

    return root;
}

function generateEmptyData(colCount: number, rowCount: number) {
    const line: string[] = new Array(colCount || 1);
    line.fill('', 0, colCount || 1);

    const result: string[][] = new Array(rowCount || 1);
    result.fill(line, 0, rowCount || 1);

    return result;
}

function convertResponse(dataset: any, dataLayout: IMultiAxisDataServiceDataLayout): MultiAxisDatasetData {
    const { dataset: { axes: [colAxis, rowAxis], data }, params, totalCounts } = dataset;
    const { rows, columns } = dataLayout;

    const rowsIsZero = !params.offset[1];
    const colsIsZero = !params.offset[0];

    const rowsIsEnd = params.offset[1] + params.pageSize[1] >= totalCounts[1];
    const colsIsEnd = params.offset[0] + params.pageSize[0] >= totalCounts[0];

    const rowsNode = convertResponseAxis(rowAxis, rows, rowsIsZero, rowsIsEnd);
    const columnsNode = convertResponseAxis(colAxis, columns, colsIsZero, colsIsEnd);

    const aggregations = data || generateEmptyData(totalCounts[1], totalCounts[0]);

    return {
        rows: rowsNode,
        columns: columnsNode,
        aggregations
    }
}

// first level expand/collapse hack
function fixFirstLevelExpandCollapse(query: any, dataLayout: IMultiAxisDataServiceDataLayout) {
    if (dataLayout.columns.items[0] && !dataLayout.columns.items[0].expanded) {
        query.params.pageSize[0] = MAX_AMOUNT;
    }

    if (dataLayout.rows.items[0] && !dataLayout.rows.items[0].expanded) {
        query.params.pageSize[1] = MAX_AMOUNT;
    }

    const rowLevelExps = query.query.groupBy.rows.expansions.filter((expansion: any) => (expansion.level));
    const colLevelExps = query.query.groupBy.columns.expansions.filter((expansion: any) => (expansion.level));

    for (let i = 0; i < rowLevelExps.length - 1; i += 1) {
        rowLevelExps[i].level.expanded = rowLevelExps[i + 1].level.expanded;
    }

    // for (let i = rowLevelExps.length - 2;  i >= 0 ; i -= 1) {
    //     if (rowLevelExps[i + 1].level.expanded){
    //         rowLevelExps[i].level.expanded = true;
    //     }
    // }
    //
    // for (let i = 1; i< dataLayout.rows.items.length; i+=1){
    //     if (dataLayout.rows.items[i].excludeSummary){
    //         rowLevelExps[i - 1].level.expanded = true;
    //     }
    // }

    for (let i = 0; i < colLevelExps.length - 1; i += 1) {
        colLevelExps[i].level.expanded = colLevelExps[i + 1].level.expanded;
    }

    // for (let i = colLevelExps.length - 2; i >= 0; i -= 1) {
    //     if (colLevelExps[i + 1].level.expanded){
    //         colLevelExps[i].level.expanded = true;
    //     }
    // }
    //
    // for (let i = 1; i< dataLayout.columns.items.length; i+=1){
    //     if (dataLayout.columns.items[i].excludeSummary){
    //         colLevelExps[i - 1].level.expanded = true;
    //     }
    // }
}

export class QueryExecutionMultiAxisDataService implements IMultiAxisDataService {
    data: JQueryDeferred<IMultiAxisDataset>;

    executions: Execution[];

    constructor() {
        this.data = $.Deferred().resolve();
        this.executions = [];
    }

    private addExecution(exec: Execution) {
        const exisiting = this.executions.filter((exe) => (exe.state === 'existing'));

        this.executions = [exec].concat(this.executions.filter((exe) => (exe.state === 'new')));

        setTimeout(() => {
            exisiting.forEach(this.removeExecution)
        });
    }

    private createNewExecution(dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, rowsCount: number, columnsCount: number, state: IState): JQueryDeferred<any> {
        const query: any = generateQuery(dataLayout, startIndexRows, startIndexColumns, rowsCount, columnsCount, state);
        const code = paginationCode(startIndexColumns, startIndexRows, columnsCount, rowsCount);
        fixFirstLevelExpandCollapse(query, dataLayout);

        const exec: Execution = {
            layout: dataLayout,
            state: 'new',
            id: $.Deferred(),
            server: state.server,
            calls: {
                [code]: request({
                    url: state.server.concat(`/rest_v2/queryExecutions${state?.refreshData ? '?freshData=true' : ''}`),
                    headers: {
                        Accept: 'application/MultiAxisData+json',
                        'Content-Type': 'application/execution.MultiAxisQuery+json'
                    },
                    data: JSON.stringify(query),
                    method: 'POST',
                    processData: false
                }).done((data: any, status: string, xhr: XMLHttpRequest) => {
                    const location = xhr.getResponseHeader('Content-Location');
                    if (location) {
                        exec.state = 'existing';
                        exec.id.resolve(location);
                        exec.server = state.server;
                    }
                })
            }
        };

        this.addExecution(exec);

        return exec.calls[code];
    }

    private callExistingExecution(id: string, startIndexRows: number, startIndexColumns: number, rowsCount: number, columnsCount: number, state: IState) {
        return request({
            url: `${state.server}/${id}?offset=${startIndexColumns}&offset=${startIndexRows}&pageSize=${columnsCount || MAX_AMOUNT}&pageSize=${rowsCount || MAX_AMOUNT}`,
            headers: {
                Accept: 'application/MultiAxisData+json'
            },
            processData: false
        });
    }

    private getAsyncResult(dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, rowsCount: number, columnsCount: number, state: IState): JQueryDeferred<any> | null {
        let result: JQueryDeferred<any> | null = null;

        const exec: Execution | undefined = this.executions.find((execution) => (areLayoutsSame(execution.layout, dataLayout)));

        if (exec) {
            // first level expand/collapse hack
            const rowsLimit = dataLayout.rows.items[0] && dataLayout.rows.items[0].expanded ? rowsCount : 0;
            const colsLimit = dataLayout.columns.items[0] && dataLayout.columns.items[0].expanded ? columnsCount : 0;
            const code = paginationCode(startIndexColumns, startIndexRows, columnsCount, rowsCount);

            if (!exec.calls[code]) {
                exec.calls[code] = $.Deferred();
                result = exec.calls[code];

                exec.id.done((id) => {
                    this.callExistingExecution(id, startIndexRows, startIndexColumns, rowsLimit, colsLimit, state)
                        .done((data: any, status: string, xhr: XMLHttpRequest) => {
                            result!.resolve(data, status, xhr);
                        })
                        .fail((a: any, b: any, c: any) => {
                            result!.reject(a, b, c);
                        });
                });
            }
        } else {
            result = this.createNewExecution(dataLayout, startIndexRows, startIndexColumns, rowsCount, columnsCount, state);
        }

        return result;
    }

    private removeExecution(execution: Execution) {
        const res = $.Deferred();
        execution.id.done((id) => {
            request({
                url: `${execution.server}/${id.split('/data')[0]}`,
                method: 'DELETE'
            }).always(() => {
                res.resolve();
            });
        })

        return res;
    }

    reset() {
        const res = $.Deferred();
        const removals = this.executions.map(this.removeExecution);
        this.executions = [];

        $.when(...removals).always(() => {
            res.resolve();
        });

        return res;
    }

    loadData(dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, rowsCount: number, columnsCount: number, state: IState): JQueryDeferred<IMultiAxisDataset> {
        const asyncResult = this.getAsyncResult(dataLayout, startIndexRows, startIndexColumns, rowsCount, columnsCount, state);
        const dfd = $.Deferred();

        if (asyncResult) {
            const previousCall = this.data;
            this.data = dfd;

            asyncResult.done((response: any) => {
                previousCall.always(() => {
                    const data = convertResponse(response, dataLayout);
                    dfd.resolve({
                        summary: {
                            ...dataLayout,
                            empty: !(response.dataset && response.dataset.data),
                            complete: false
                        } as any,
                        data
                    });
                });
            }).fail((error) => {
                previousCall.always(() => {
                    error = biComponentErrorFactory.requestError(error);
                    dfd.reject(error);
                });
            });
        }

        return dfd;
    }
}
